import { CustomLabel } from "types/CustomLabel";
import { REQUIREMENT } from "helpers/constants";
import {
  getRequirementEvaluationMetrics,
  getSkillLevelRatingOptions,
} from "helpers/customLabels";
import { Requirement } from "types/requirement";
import { Site } from "types/site";
import { Species } from "types/species";
import { Tag } from "types/tag";
import { User } from "types/user";
import { ACTIONS, STEPS } from "../config";
import { RequestFeedbackAction, RequestFeedbackData } from "../types";
import {
  matchNamedOption,
  matchNamedOptions,
  NamedOption,
  parseDate,
  parseRating,
  parseTime,
  parseYesNo,
  personNameAliases,
  requirementNameAliases,
} from "./parseUtterance";

export type VoiceFieldKind =
  | "text"
  | "pick"
  | "picks"
  | "date"
  | "time"
  | "rating"
  | "bool"
  | "advance"
  | "review";

export type VoiceField = {
  id: string;
  prompt: string;
  kind: VoiceFieldKind;
  required?: boolean;
  options?: NamedOption[];
  ratingOptions?: { value: number; label: string }[];
  iterRequirementId?: string;
};

export type VoiceAgentContext = {
  step: string;
  data: RequestFeedbackData;
  isCoachInitiated: boolean;
  coaches: User[];
  learners: User[];
  requirements: Requirement[];
  species: Species[];
  sites: Site[];
  tags: Tag[];
  customLabels: CustomLabel[];
};

const personOption = (user: User): NamedOption => ({
  id: user.user_id || user.email || user.name,
  name: user.name,
  aliases: personNameAliases(user.name, user.email),
});

const iterSubRequirements = (data: RequestFeedbackData) =>
  (data.selectedCompetencySkill?.competencies || [])
    .map((row: any) => row.competency)
    .filter(Boolean);

export const ratingOptionsFor = (
  requirement: Requirement | null,
  customLabels: CustomLabel[],
) =>
  getSkillLevelRatingOptions(
    requirement?.type,
    getRequirementEvaluationMetrics(requirement),
    customLabels,
  );

const ratingHintFor = (
  requirement: Requirement | null,
  customLabels: CustomLabel[],
) =>
  ratingOptionsFor(requirement, customLabels)
    .map((option) => `${option.value}, ${option.name || option.label}`)
    .join(". ");

const isFilled = (value: unknown) => {
  if (value == null) return false;
  if (typeof value === "boolean") return true;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const getVoiceFields = (ctx: VoiceAgentContext): VoiceField[] => {
  const { step, data, isCoachInitiated } = ctx;
  const isITER =
    data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;
  const ratings = ratingOptionsFor(data.selectedCompetencySkill, ctx.customLabels);
  const ratingHint = ratingHintFor(data.selectedCompetencySkill, ctx.customLabels);

  if (step === STEPS.REQUEST2) {
    const fields: VoiceField[] = isCoachInitiated
      ? [
          {
            id: "selectedLearner",
            kind: "pick",
            required: true,
            prompt: "Which learner is this feedback for? Say their name.",
            options: ctx.learners.map(personOption),
          },
        ]
      : [
          {
            id: "selectedCoach",
            kind: "pick",
            required: true,
            prompt: "Which coach should receive this request? Say their name.",
            options: ctx.coaches.map(personOption),
          },
        ];
    fields.push({
      id: "selectedCompetencySkill",
      kind: "pick",
      required: true,
      prompt:
        "Which EPA, competency, or ITER is this about? You can say the name or number, like EPA 1.",
      options: ctx.requirements.map((requirement) => ({
        id: requirement.requirement_id || requirement.name,
        name: requirement.name,
        aliases: requirementNameAliases(requirement.name, requirement.type),
      })),
    });
    fields.push({
      id: "advance",
      kind: "advance",
      prompt: "This step is ready. Say next when you want to continue.",
    });
    return fields;
  }

  if (step === STEPS.REQUEST) {
    return [
      {
        id: "name",
        kind: "text",
        required: true,
        prompt: "What is the patient's name?",
      },
      {
        id: "mrn",
        kind: "text",
        prompt: "Any additional patient information, like an MRN? You can skip this.",
      },
      {
        id: "caseDate",
        kind: "date",
        required: true,
        prompt: isCoachInitiated
          ? "What date was the case? You can say today, yesterday, or a date."
          : "What date was the case? It must be within the last 6 days to preserve accuracy and reduce recall bias.",
      },
      {
        id: "caseTime",
        kind: "time",
        required: true,
        prompt: "About what time was the case?",
      },
      {
        id: "advance",
        kind: "advance",
        prompt: "Patient identity looks good. Say next to continue.",
      },
    ];
  }

  if (step === STEPS.REQUEST1) {
    return [
      {
        id: "selectedSpecies",
        kind: "pick",
        prompt: "What species is the patient?",
        options: ctx.species.map((species) => ({
          id: species.species_id || species.name,
          name: species.name,
        })),
      },
      {
        id: "animalSex",
        kind: "pick",
        prompt:
          "What is the animal's sex? Female, female spayed, male, or male castrated.",
        options: [
          { id: "Female", name: "Female" },
          { id: "Female Spayed", name: "Female Spayed" },
          { id: "Male", name: "Male" },
          { id: "Male Castrated", name: "Male Castrated" },
        ],
      },
      {
        id: "selectedSite",
        kind: "pick",
        prompt: "Which site or clinic was this at? You can skip if none apply.",
        options: ctx.sites.map((site) => ({
          id: site.site_id || site.name,
          name: site.name,
        })),
      },
      {
        id: "course",
        kind: "text",
        prompt: "Any course or specialty to note? You can skip this.",
      },
      {
        id: "advance",
        kind: "advance",
        prompt: "Say next to review the rubric, or skip any leftover optional fields.",
      },
    ];
  }

  if (step === STEPS.PERFORM) {
    return [
      {
        id: "advance",
        kind: "advance",
        prompt: isCoachInitiated
          ? "Take a look at the rubric, then say begin when you are ready to give feedback."
          : "Take a look at the rubric, then say begin when you are ready to reflect.",
      },
    ];
  }

  if (step === STEPS.REFLECT) {
    if (isITER) {
      const fields: VoiceField[] = [];
      for (const sub of iterSubRequirements(data)) {
        fields.push({
          id: `iterComment:${sub.requirement_id}`,
          kind: "text",
          required: true,
          iterRequirementId: sub.requirement_id,
          prompt: `For ${sub.name}, what is your reflection?`,
        });
        fields.push({
          id: `iterRate:${sub.requirement_id}`,
          kind: "rating",
          required: true,
          iterRequirementId: sub.requirement_id,
          ratingOptions: ratingOptionsFor(sub, ctx.customLabels),
          prompt: `How would you rate yourself on ${sub.name}? ${ratingHintFor(sub, ctx.customLabels)}.`,
        });
      }
      fields.push({
        id: "coachAvailable",
        kind: "bool",
        required: true,
        prompt: "Is your coach with you right now?",
      });
      fields.push({
        id: "advance",
        kind: "advance",
        prompt: "Reflection is ready. Say next to continue.",
      });
      return fields;
    }

    return [
      {
        id: "perform",
        kind: "text",
        required: true,
        prompt: "In your own words, how did the performance go?",
      },
      {
        id: "rate",
        kind: "rating",
        required: true,
        ratingOptions: ratings,
        prompt: `How would you rate yourself? ${ratingHint}.`,
      },
      {
        id: "coachAvailable",
        kind: "bool",
        required: true,
        prompt: "Is your coach with you right now?",
      },
      {
        id: "advance",
        kind: "advance",
        prompt: "Reflection is ready. Say next to continue.",
      },
    ];
  }

  if (step === STEPS.REFLECT_COACH) {
    const fields: VoiceField[] = [
      {
        id: "selectedFocusFeedback",
        kind: "picks",
        prompt:
          "What should this feedback focus on? You can name tags like communication or execution.",
        options: ctx.tags.map((tag) => ({
          id: tag.tag_id || tag.name,
          name: tag.name,
        })),
      },
    ];

    if (isITER) {
      for (const sub of iterSubRequirements(data)) {
        fields.push({
          id: `iterCoachRate:${sub.requirement_id}`,
          kind: "rating",
          required: true,
          iterRequirementId: sub.requirement_id,
          ratingOptions: ratingOptionsFor(sub, ctx.customLabels),
          prompt: `Coach rating for ${sub.name}? ${ratingHintFor(sub, ctx.customLabels)}.`,
        });
      }
    } else {
      fields.push({
        id: "coachObserve",
        kind: "text",
        required: true,
        prompt: isCoachInitiated
          ? "What feedback do you want to give the learner?"
          : "What did you observe, coach?",
      });
      fields.push({
        id: "coachRate",
        kind: "rating",
        required: true,
        ratingOptions: ratings,
        prompt: `What rating would you give? ${ratingHint}.`,
      });
    }

    fields.push({
      id: "advance",
      kind: "advance",
      prompt: "Feedback notes are in. Say next to review before submitting.",
    });
    return fields;
  }

  if (step === STEPS.SUBMIT) {
    return [
      {
        id: "review",
        kind: "review",
        prompt:
          "Everything is on the review screen. When it looks right, tap Submit. I will not submit for you.",
      },
    ];
  }

  return [];
};

const fieldValue = (field: VoiceField, data: RequestFeedbackData): unknown => {
  if (field.id.startsWith("iterComment:") && field.iterRequirementId) {
    return data.iterSubRequirementComments[field.iterRequirementId];
  }
  if (field.id.startsWith("iterRate:") && field.iterRequirementId) {
    return data.iterSubRequirementRates[field.iterRequirementId];
  }
  if (field.id.startsWith("iterCoachRate:") && field.iterRequirementId) {
    return data.iterSubRequirementCoachRates[field.iterRequirementId];
  }
  return (data as Record<string, unknown>)[field.id];
};

export const nextVoiceField = (
  ctx: VoiceAgentContext,
  skippedIds: Set<string>,
  justFilledIds: Set<string> = new Set(),
): VoiceField | null => {
  const fields = getVoiceFields(ctx);
  return (
    fields.find((field) => {
      if (field.kind === "advance" || field.kind === "review") {
        const pending = fields.filter(
          (item) =>
            item.kind !== "advance" &&
            item.kind !== "review" &&
            item.required &&
            !justFilledIds.has(item.id) &&
            !isFilled(fieldValue(item, ctx.data)),
        );
        return pending.length === 0;
      }
      if (justFilledIds.has(field.id)) return false;
      if (skippedIds.has(field.id) && !field.required) return false;
      return !isFilled(fieldValue(field, ctx.data));
    }) || null
  );
};

export const applyUtterance = (
  field: VoiceField,
  text: string,
  ctx: VoiceAgentContext,
): { action?: RequestFeedbackAction; confirm: string; ok: boolean } => {
  const spoken = text.trim();

  if (field.kind === "text") {
    if (field.id.startsWith("iterComment:") && field.iterRequirementId) {
      return {
        ok: true,
        confirm: "Saved that reflection.",
        action: {
          type: ACTIONS.SET_ITER_SUB_REQUIREMENT_COMMENT,
          payload: {
            requirementId: field.iterRequirementId,
            comment: spoken,
          },
        },
      };
    }
    const actionType =
      field.id === "name"
        ? ACTIONS.SET_NAME
        : field.id === "mrn"
          ? ACTIONS.SET_MRN
          : field.id === "course"
            ? ACTIONS.SET_COURSE
            : field.id === "perform"
              ? ACTIONS.SET_PERFORM
              : field.id === "coachObserve"
                ? ACTIONS.SET_COACH_OBSERVE
                : null;
    if (!actionType) {
      return { ok: false, confirm: "I could not save that field." };
    }
    return {
      ok: true,
      confirm: "Got it.",
      action: { type: actionType, payload: spoken },
    };
  }

  if (field.kind === "bool") {
    const value = parseYesNo(spoken);
    if (value == null) {
      return { ok: false, confirm: "Please say yes or no." };
    }
    return {
      ok: true,
      confirm: value ? "I'll keep the coach on this request." : "Okay, they can review later.",
      action: { type: ACTIONS.SET_COACH_AVAILABLE, payload: value },
    };
  }

  if (field.kind === "date") {
    const value = parseDate(spoken);
    if (!value) {
      return { ok: false, confirm: "I did not catch the date. Try today, or August 10." };
    }
    return {
      ok: true,
      confirm: `Date set to ${value.format("MMM D, YYYY")}.`,
      action: { type: ACTIONS.SET_CASE_DATE, payload: value },
    };
  }

  if (field.kind === "time") {
    const value = parseTime(spoken);
    if (!value) {
      return { ok: false, confirm: "I did not catch the time. Try 2 PM or 14:30." };
    }
    return {
      ok: true,
      confirm: `Time set to ${value.format("h:mm A")}.`,
      action: { type: ACTIONS.SET_CASE_TIME, payload: value },
    };
  }

  if (field.kind === "rating") {
    const value = parseRating(spoken, field.ratingOptions || []);
    if (value == null) {
      return {
        ok: false,
        confirm: "Please say a number from the rubric, or the level name.",
      };
    }
    if (field.id.startsWith("iterRate:") && field.iterRequirementId) {
      return {
        ok: true,
        confirm: `Rated ${value}.`,
        action: {
          type: ACTIONS.SET_ITER_SUB_REQUIREMENT_RATE,
          payload: { requirementId: field.iterRequirementId, rate: value },
        },
      };
    }
    if (field.id.startsWith("iterCoachRate:") && field.iterRequirementId) {
      return {
        ok: true,
        confirm: `Coach rating ${value}.`,
        action: {
          type: ACTIONS.SET_ITER_SUB_REQUIREMENT_COACH_RATE,
          payload: { requirementId: field.iterRequirementId, rate: value },
        },
      };
    }
    const type =
      field.id === "coachRate" ? ACTIONS.SET_COACH_RATE : ACTIONS.SET_RATE;
    return {
      ok: true,
      confirm: `Rated ${value}.`,
      action: { type, payload: value },
    };
  }

  if (field.kind === "pick") {
    const match = matchNamedOption(spoken, field.options || []);
    if (!match) {
      const names = (field.options || [])
        .map((option) => option.name)
        .filter(Boolean);
      const heard = spoken.length > 40 ? `${spoken.slice(0, 40)}...` : spoken;
      const hint = names.length
        ? ` I heard "${heard}". Try ${names.slice(0, 4).join(", ")}${
            names.length > 4 ? ", or another name from the list" : ""
          }.`
        : " Try saying the name again.";
      return {
        ok: false,
        confirm: `I could not match that.${hint}`,
      };
    }
    if (field.id === "selectedCoach") {
      const coach = ctx.coaches.find(
        (user) => (user.user_id || user.email || user.name) === match.id,
      );
      return {
        ok: !!coach,
        confirm: coach ? `Coach set to ${coach.name}.` : "I could not find that coach.",
        action: coach
          ? { type: ACTIONS.SET_SELECTED_COACH, payload: coach }
          : undefined,
      };
    }
    if (field.id === "selectedLearner") {
      const learner = ctx.learners.find(
        (user) => (user.user_id || user.email || user.name) === match.id,
      );
      return {
        ok: !!learner,
        confirm: learner
          ? `Learner set to ${learner.name}.`
          : "I could not find that learner.",
        action: learner
          ? { type: ACTIONS.SET_SELECTED_LEARNER, payload: learner }
          : undefined,
      };
    }
    if (field.id === "selectedCompetencySkill") {
      const requirement = ctx.requirements.find(
        (item) => (item.requirement_id || item.name) === match.id,
      );
      return {
        ok: !!requirement,
        confirm: requirement
          ? `Requirement set to ${requirement.name}.`
          : "I could not find that requirement.",
        action: requirement
          ? { type: ACTIONS.SET_COMPETENCY_SKILLS, payload: requirement }
          : undefined,
      };
    }
    if (field.id === "selectedSpecies") {
      const species = ctx.species.find(
        (item) => (item.species_id || item.name) === match.id,
      );
      return {
        ok: !!species,
        confirm: species ? `Species set to ${species.name}.` : "I could not match that species.",
        action: species ? { type: ACTIONS.SET_SPECIES, payload: species } : undefined,
      };
    }
    if (field.id === "selectedSite") {
      const site = ctx.sites.find(
        (item) => (item.site_id || item.name) === match.id,
      );
      return {
        ok: !!site,
        confirm: site ? `Site set to ${site.name}.` : "I could not match that site.",
        action: site ? { type: ACTIONS.SET_SITE, payload: site } : undefined,
      };
    }
    if (field.id === "animalSex") {
      return {
        ok: true,
        confirm: `Sex set to ${match.name}.`,
        action: { type: ACTIONS.SET_SEX, payload: match.id },
      };
    }
  }

  if (field.kind === "picks") {
    const matches = matchNamedOptions(spoken, field.options || []);
    if (matches.length === 0) {
      return {
        ok: false,
        confirm: "I did not catch a focus tag. Try communication, or skip.",
      };
    }
    const tags = ctx.tags.filter((tag) =>
      matches.some((match) => (tag.tag_id || tag.name) === match.id),
    );
    return {
      ok: tags.length > 0,
      confirm: `Focus set to ${tags.map((tag) => tag.name).join(", ")}.`,
      action: { type: ACTIONS.SET_SELECTED_FOCUS_FEEDBACK, payload: tags },
    };
  }

  return { ok: false, confirm: "I did not understand that." };
};

export const nextStepFor = (
  step: string,
  data: RequestFeedbackData,
  isCoachInitiated: boolean,
): { step?: string; begin?: boolean } => {
  const isITER =
    data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

  if (step === STEPS.REQUEST2) {
    return { step: isITER ? STEPS.PERFORM : STEPS.REQUEST };
  }
  if (step === STEPS.REQUEST) return { step: STEPS.REQUEST1 };
  if (step === STEPS.REQUEST1) return { step: STEPS.PERFORM };
  if (step === STEPS.PERFORM) return { begin: true };
  if (step === STEPS.REFLECT) {
    return {
      step: data.coachAvailable ? STEPS.REFLECT_COACH : STEPS.SUBMIT,
    };
  }
  if (step === STEPS.REFLECT_COACH) return { step: STEPS.SUBMIT };
  return {};
};
